import type { InternalAxiosRequestConfig } from "axios";
import type { UiBranding } from "@/theme/ui-branding";
import type { PublicRuntimeConfigPayload } from "@/config/public-runtime-config";

/** The subset of site-service's per-request res.locals (server/middlewares/template-locals.js) read during SSR. */
export type SsrRequestLocals = {
  uiBranding?: UiBranding;
  publicRuntimeConfig?: PublicRuntimeConfigPayload;
  /** Already escaped for an inline <script> (safeJsonStringify). */
  uiBrandingJson?: string;
  publicRuntimeConfigJson?: string;
  uiBrandingCss?: string;
  uiBrandingCssFile?: string;
  uiBrandTheme?: string | null;
  /** Only the configured keys (server/util/branding-icons.js). */
  uiBrandingIcons?: Partial<
    Record<
      "themeColor" | "png96" | "svg" | "shortcutIcon" | "appleTouchIcon" | "maskIcon" | "manifest" | "msTileImage",
      string
    >
  >;
  uiBrandingFontGoogle?: string;
  /** Set only when OneTrust is the configured cookie banner provider. */
  oneTrustDomainScript?: string;
  /** This process over loopback (http://127.0.0.1:<port>), for loaders calling site-service's own routes. */
  ssrOrigin?: string;
  /** Whether this request carries a logged-in session (req.user). */
  signedIn?: boolean;
};

declare global {
  // eslint-disable-next-line no-var
  var __SSR_REQUEST_LOCALS__: { getStore(): SsrRequestLocals | undefined } | undefined;
}

/**
 * Server render only: site-service's SSR handler (server/routes/docs, server/routes/catalog) runs each request inside an
 * AsyncLocalStorage holding that request's template locals. Always undefined in the browser.
 */
export const getSsrRequestLocals = (): SsrRequestLocals | undefined => globalThis.__SSR_REQUEST_LOCALS__?.getStore();

/** Axios request interceptor. SSR: env URLs are path-only without `location`; send them to this site-service over loopback. */
export function ssrOriginInterceptor(config: InternalAxiosRequestConfig) {
  const ssrOrigin = getSsrRequestLocals()?.ssrOrigin;
  if (ssrOrigin && config.baseURL?.startsWith("/")) {
    config.baseURL = ssrOrigin + config.baseURL;
  }
  return config;
}
